// CSV-profil-editoren: PointViews konfiguration af CSV-eksport (profiler med kolonner, datakilder, separator, decimaltegn,
// encoding, koordinatpræcision). Åbnes fra Gem-dialogens "Redigér profil" (Geonote-frontenden monterer den i en lys dialog
// via DigitizationModule.mountCsvProfileEditor()).
//
// Layout (2026-10): profillinjen øverst (vælger + Ny/Kopiér/Omdøb/Slet + Formatindstillinger), derunder to spalter:
// til venstre Kolonner (hver kolonne ÉN enhed: håndtag til drag/drop, nummer, kolonnenavn, datakilde, slet), til højre
// Preview som rigtig tabel med kolonnenumre, så preview let sammenholdes med kolonneopsætningen (hover markerer kolonnen
// begge steder). Formatindstillinger ligger i en popup, åbnet fra profillinjen. Kolonner flyttes ved at trække i håndtaget
// (eller Alt+pil op/ned på håndtaget).
//
// Design: følger PointViews designlinje (VIEWER-UI-OVERLAYS.md §B6, assets/pv-overlays.scss): temaets skrift arves (Gotham),
// --pv-* tokens (med fallback til samme værdier, så editoren også ser rigtig ud uden for Geonote), ingen inline-farver.
//
// Ren UI-orkestrering: al værdi-/formatlogik ligger uændret i digitization/export/csv/{CsvProfile,CsvProfileStore,
// csvGenerator,dataSources}.js. Preview'et genereres af SAMME buildCsvText() som selve eksporten (kun parset til en tabel).
//
// Level 2/3 (DOM-afhængig) - ikke enhedstestet; verificeres i browser (se WORKLOG).
import {csvProfileStore} from "../digitization/export/csv/CsvProfileStore.js";
import {createFullProfile, createColumn} from "../digitization/export/csv/CsvProfile.js";
import {buildCsvText} from "../digitization/export/csv/csvGenerator.js";
import {FEATURE_FIELDS, GEOMETRY_FIELDS, discoverAttributeKeys, getAttributeGroupLabel, getAttributeDisplayLabel} from "../digitization/export/csv/dataSources.js";
import {createFeature} from "../digitization/Feature.js";

import {t, T} from "../digitization/i18n/index.js";
// Vises kun i preview'et, når projektet endnu ingen registreringer har, så editoren kan bruges/demonstreres fra første
// øjeblik - det fælles specialtegn-testdatasæt (Ø315, æøå/ÆØÅ, tankestreg), så et Unicode-/encoding-problem kan ses.
function buildDemoFeatures(){
	return [
		createFeature({
			geometryType: "Point", coordinates: [723456.789, 6172345.123, 12.345],
			name: "Rør Ø315 – Søndergade æøå ÆØÅ", category: "specialized", tool: "digitize_pipe",
			attributes: {Beskrivelse: "På tværs af Åboulevarden – højre side", Materiale: "Prøve æøå", dimensionMm: 315, centerZ: 10.05},
		}),
		createFeature({
			geometryType: "LineString", coordinates: [[723400, 6172300, 12.0], [723420, 6172320, 12.5], [723450, 6172340, 13.1]],
			name: "Ledning 1", category: "basic", tool: "digitize_line",
		}),
	];
}

// ---- ikoner (Material Design Icons-stier som inline SVG: ingen skrifttype-afhængighed) ----
const ICON_PATHS = {
	plus: "M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z",
	copy: "M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z",
	pencil: "M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z",
	trash: "M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z",
	tune: "M3,17V19H9V17H3M3,5V7H13V5H3M13,21V19H21V17H13V15H11V21H13M7,9V11H3V13H7V15H9V9H7M21,13V11H11V13H21M15,9H17V7H21V5H17V3H15V9Z",
	grip: "M9,3A2,2 0 1,1 7,5A2,2 0 0,1 9,3M15,3A2,2 0 1,1 13,5A2,2 0 0,1 15,3M9,10A2,2 0 1,1 7,12A2,2 0 0,1 9,10M15,10A2,2 0 1,1 13,12A2,2 0 0,1 15,10M9,17A2,2 0 1,1 7,19A2,2 0 0,1 9,17M15,17A2,2 0 1,1 13,19A2,2 0 0,1 15,17Z",
};
function icon(name){
	return `<svg class="pv-csvp-ico" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="${ICON_PATHS[name]}"/></svg>`;
}

// ---- styling (designlinjen: arvet skrift, --pv-* tokens, bånd-overskrifter, 6px rækkeradius) ----
const STYLE_ID = "pv-csvp-style";
// Chevron på ALLE dropdowns (native <select> med appearance:none ville ellers ligne et tekstfelt)
const CHEVRON = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23757575' d='M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'/%3E%3C/svg%3E\")";
const STYLE = `
.pv-csvp, .pv-csvp-overlay { --csvp-primary: var(--pv-primary, #0073A9); --csvp-danger: var(--pv-danger, #ff5252); --csvp-border: var(--pv-border, rgba(0,0,0,.08)); --csvp-line: rgba(0,0,0,.18); --csvp-band: var(--pv-band-bg, rgba(0,0,0,.03)); --csvp-active: var(--pv-active, rgba(0,115,169,.08)); --csvp-active-hover: var(--pv-active-hover, rgba(0,115,169,.13)); --csvp-muted: var(--pv-text-muted, rgba(0,0,0,.55)); --csvp-radius: var(--pv-radius-row, 6px); font-family: inherit; font-size: 13px; line-height: 1.45; color: rgba(0,0,0,.87); }
.pv-csvp *, .pv-csvp *::before, .pv-csvp *::after, .pv-csvp-overlay *, .pv-csvp-overlay *::before, .pv-csvp-overlay *::after { box-sizing: border-box; font-family: inherit; }
.pv-csvp h3 { margin: 0; font-size: var(--pv-band-size, 12px); font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: rgba(0,0,0,.65); }
.pv-csvp-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 10px; padding: 8px 12px; margin-bottom: 12px; background: var(--csvp-band); border: 1px solid var(--csvp-border); border-radius: var(--csvp-radius); }
.pv-csvp-bar-label { font-size: var(--pv-band-size, 12px); font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: rgba(0,0,0,.65); }
.pv-csvp-bar .pv-csvp-select { flex: 0 1 260px; min-width: 160px; }
.pv-csvp-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; flex: 1 1 auto; }
.pv-csvp-sep { width: 1px; height: 20px; margin: 0 4px; background: var(--csvp-line); }
.pv-csvp-status { margin: 0 0 12px; padding: 8px 12px; border-radius: var(--csvp-radius); font-size: 12.5px; background: var(--csvp-active); color: var(--csvp-primary); }
.pv-csvp-grid { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr); gap: 12px; align-items: start; }
@media (max-width: 980px) { .pv-csvp-grid { grid-template-columns: minmax(0, 1fr); } }
.pv-csvp-card { min-width: 0; background: #fff; border: 1px solid var(--csvp-border); border-radius: var(--csvp-radius); overflow: hidden; }
.pv-csvp-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 40px; padding: 4px 12px; background: var(--csvp-band); border-bottom: 1px solid var(--csvp-border); }
.pv-csvp-card-body { padding: 10px 12px 12px; }
.pv-csvp-count { margin-left: 8px; padding: 0 7px; border-radius: var(--pv-radius-badge, 10px); font-size: 10.5px; font-weight: 600; background: var(--pv-badge-bg, rgba(0,115,169,.12)); color: var(--csvp-primary); letter-spacing: 0; }
.pv-csvp-settings { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 16px; }
.pv-csvp-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.pv-csvp-field > span:first-child { font-size: 12px; color: var(--csvp-muted); }
.pv-csvp-inline { display: flex; gap: 6px; }
.pv-csvp input[type=text], .pv-csvp input[type=number], .pv-csvp select, .pv-csvp-overlay input[type=text], .pv-csvp-overlay input[type=number], .pv-csvp-overlay select { width: 100%; height: 32px; padding: 0 8px; font: inherit; color: rgba(0,0,0,.87); background-color: #fff; border: 1px solid var(--csvp-line); border-radius: 4px; outline: none; }
.pv-csvp select, .pv-csvp-overlay select { appearance: none; -webkit-appearance: none; -moz-appearance: none; padding-right: 30px; text-overflow: ellipsis; cursor: pointer; background-image: ${CHEVRON}; background-repeat: no-repeat; background-position: right 6px center; background-size: 18px 18px; }
.pv-csvp input:hover, .pv-csvp select:hover, .pv-csvp-overlay input:hover, .pv-csvp-overlay select:hover { border-color: rgba(0,0,0,.4); }
.pv-csvp input:focus, .pv-csvp select:focus, .pv-csvp-overlay input:focus, .pv-csvp-overlay select:focus { border-color: var(--csvp-primary); box-shadow: 0 0 0 2px var(--csvp-active-hover); }
.pv-csvp input::placeholder, .pv-csvp-overlay input::placeholder { color: rgba(0,0,0,.4); }
.pv-csvp-check { flex-direction: row; align-items: center; gap: 8px; min-height: 32px; cursor: pointer; align-self: end; }
.pv-csvp-overlay .pv-csvp-check input { width: 16px; height: 16px; margin: 0; accent-color: var(--csvp-primary); }
.pv-csvp-check > span { font-size: 13px !important; color: inherit !important; }
.pv-csvp-btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; font: inherit; font-weight: 500; color: var(--csvp-primary); background: #fff; border: 1px solid var(--csvp-line); border-radius: 4px; cursor: pointer; white-space: nowrap; }
.pv-csvp-btn:hover { background: var(--csvp-active); }
.pv-csvp-btn:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--csvp-active-hover); }
.pv-csvp-btn--primary { color: #fff; background: var(--csvp-primary); border-color: var(--csvp-primary); }
.pv-csvp-btn--primary:hover { background: var(--csvp-primary); filter: brightness(.92); }
.pv-csvp-btn--danger { color: var(--csvp-danger); }
.pv-csvp-btn--danger:hover { background: rgba(255,82,82,.1); }
.pv-csvp-btn--danger.pv-csvp-btn--solid { color: #fff; background: var(--csvp-danger); border-color: var(--csvp-danger); }
.pv-csvp-btn--danger.pv-csvp-btn--solid:hover { background: var(--csvp-danger); filter: brightness(.92); }
.pv-csvp-btn--plain { color: rgba(0,0,0,.7); }
.pv-csvp-btn--plain:hover { background: var(--pv-hover, rgba(0,0,0,.05)); }
.pv-csvp-ico { flex: 0 0 auto; }
.pv-csvp-colhead, .pv-csvp-col { display: grid; grid-template-columns: 22px 24px minmax(110px, 1fr) minmax(160px, 1.4fr) 32px; gap: 8px; align-items: start; }
.pv-csvp-colhead { padding: 0 8px 6px; font-size: 12px; color: var(--csvp-muted); }
.pv-csvp-columns { display: flex; flex-direction: column; gap: 6px; max-height: min(56vh, 520px); overflow-y: auto; padding: 6px 2px; margin: -4px -2px 0; }
.pv-csvp-col { position: relative; padding: 8px; background: #fff; border: 1px solid var(--csvp-border); border-radius: var(--csvp-radius); }
.pv-csvp-col:hover, .pv-csvp-col.is-hot { background: var(--csvp-active); }
.pv-csvp-col.is-dragging { z-index: 3; opacity: .92; background: #fff; box-shadow: 0 4px 14px rgba(0,0,0,.22); }
.pv-csvp-col.drop-before::before, .pv-csvp-col.drop-after::after { content: ""; position: absolute; left: 0; right: 0; height: 3px; border-radius: 2px; background: var(--csvp-primary); pointer-events: none; }
.pv-csvp-col.drop-before::before { top: -5px; }
.pv-csvp-col.drop-after::after { bottom: -5px; }
.pv-csvp-handle { touch-action: none; display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 32px; padding: 0; color: rgba(0,0,0,.4); background: transparent; border: none; border-radius: 4px; cursor: grab; }
.pv-csvp-handle:hover { color: rgba(0,0,0,.75); background: var(--pv-hover, rgba(0,0,0,.05)); }
.pv-csvp-handle:active { cursor: grabbing; }
.pv-csvp-handle:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--csvp-active-hover); color: var(--csvp-primary); }
.pv-csvp-col-no { display: inline-flex; align-items: center; justify-content: center; height: 32px; font-size: 12px; font-weight: 700; color: var(--csvp-primary); }
.pv-csvp-col-source { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.pv-csvp-iconbtn { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; padding: 0; color: rgba(0,0,0,.55); background: transparent; border: none; border-radius: 4px; cursor: pointer; }
.pv-csvp-iconbtn:hover { background: rgba(255,82,82,.12); color: var(--csvp-danger); }
.pv-csvp-iconbtn:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--csvp-active-hover); }
.pv-csvp-empty { padding: 18px; text-align: center; color: var(--csvp-muted); border: 1px dashed var(--csvp-line); border-radius: var(--csvp-radius); }
.pv-csvp-card--preview { position: sticky; top: 0; }
.pv-csvp-note { margin: 0 0 8px; font-size: 12px; color: var(--csvp-muted); }
.pv-csvp-note b { font-weight: 600; color: rgba(0,0,0,.7); }
.pv-csvp-preview { overflow: auto; max-height: min(62vh, 540px); border: 1px solid var(--csvp-border); border-radius: var(--csvp-radius); background: #fff; }
.pv-csvp-table { border-collapse: collapse; width: max-content; min-width: 100%; font-size: 12px; }
.pv-csvp-table th, .pv-csvp-table td { padding: 5px 10px; text-align: left; white-space: nowrap; border-bottom: 1px solid var(--csvp-border); border-right: 1px solid var(--csvp-border); }
.pv-csvp-table td { font-family: Consolas, "Courier New", monospace; }
.pv-csvp-table thead th { position: sticky; background: #f4f5f6; font-weight: 600; color: rgba(0,0,0,.8); z-index: 1; }
.pv-csvp-table thead tr:nth-child(1) th { top: 0; font-size: 11px; font-weight: 600; color: var(--csvp-primary); background: #eef5f9; text-align: center; padding: 3px 10px; }
.pv-csvp-table thead tr:nth-child(2) th { top: 24px; }
.pv-csvp-table td.is-hot, .pv-csvp-table th.is-hot { background: var(--csvp-active-hover); }
.pv-csvp-table tbody tr:nth-child(even) td { background-color: rgba(0,0,0,.02); }
.pv-csvp-table tbody tr:nth-child(even) td.is-hot { background-color: var(--csvp-active-hover); }
.pv-csvp-overlay { position: fixed; inset: 0; z-index: 10002; pointer-events: auto; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,.4); }
.pv-csvp-modal { width: 400px; max-width: calc(100vw - 32px); max-height: calc(100vh - 32px); display: flex; flex-direction: column; background: #fff; color: rgba(0,0,0,.87); border-radius: 4px; box-shadow: var(--pv-elevation-overlay, 0 7px 8px -4px rgba(0,0,0,.2), 0 12px 17px 2px rgba(0,0,0,.14), 0 5px 22px 4px rgba(0,0,0,.12)); }
.pv-csvp-modal--wide { width: 500px; }
.pv-csvp-modal-title { margin: 0; padding: 12px 16px; font-size: var(--pv-title-size, 15px); font-weight: var(--pv-title-weight, 500); border-bottom: 1px solid var(--csvp-border); }
.pv-csvp-modal-body { padding: 16px; overflow-y: auto; min-height: 0; }
.pv-csvp-modal-body p { margin: 0; }
.pv-csvp-modal-body > input[type=text] { height: 36px; }
.pv-csvp-modal-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 10px 16px; border-top: 1px solid var(--csvp-border); }
`;

function ensureStyle(){
	if(document.getElementById(STYLE_ID)) return;
	const el = document.createElement("style");
	el.id = STYLE_ID;
	el.textContent = STYLE;
	document.head.appendChild(el);
}

// Overlayet SKAL ligge inde i den omgivende Vuetify-dialog (.v-dialog__content): dialogens retain-focus flytter ellers fokus
// tilbage fra alt uden for dialogen (en prompt på document.body kunne ikke skrives i), og et klik uden for dialogen ville lukke
// den. Uden for en Vuetify-dialog (fx testside) bruges document.body.
function overlayHost(anchor){
	const content = anchor && anchor.closest ? anchor.closest(".v-dialog__content") : null;
	return content || document.body;
}

// Lille lokal modal (prompt/bekræft/popup) - bevidst IKKE window.prompt()/confirm(): ingen native browser-dialoger i
// Digitalisering (de blokerer scripts), og de skal matche dialogstilen. `body` (element) bruges til popup'en Formatindstillinger
// (bredere, kun OK-knap - ændringerne gælder med det samme).
function openModal({host, title, message = "", inputValue = null, body = null, okLabel = "OK", okClass = "pv-csvp-btn--primary", cancelLabel = t("Annullér")}){
	return new Promise((resolve) => {
		const overlay = document.createElement("div");
		overlay.className = "pv-csvp-overlay";
		const box = document.createElement("div");
		box.className = "pv-csvp-modal" + (body ? " pv-csvp-modal--wide" : "");
		box.setAttribute("role", "dialog");
		box.setAttribute("aria-modal", "true");
		box.innerHTML = `
			<h4 class="pv-csvp-modal-title"></h4>
			<div class="pv-csvp-modal-body">
				${message ? "<p></p>" : ""}
				${inputValue !== null ? '<input type="text" data-field="value" />' : ""}
			</div>
			<div class="pv-csvp-modal-actions">
				${cancelLabel ? '<button type="button" class="pv-csvp-btn pv-csvp-btn--plain" data-action="cancel"></button>' : ""}
				<button type="button" class="pv-csvp-btn ${okClass}" data-action="ok"></button>
			</div>`;
		box.querySelector("h4").textContent = title;
		if(message) box.querySelector("p").textContent = message;
		if(body) box.querySelector(".pv-csvp-modal-body").appendChild(body);
		box.querySelector('[data-action="ok"]').textContent = okLabel;
		const cancelBtn = box.querySelector('[data-action="cancel"]');
		if(cancelBtn) cancelBtn.textContent = cancelLabel;
		const input = box.querySelector('[data-field="value"]');
		if(input) input.value = inputValue;
		overlay.appendChild(box);
		host.appendChild(overlay);

		const dismissed = input ? null : false;
		const finish = (value) => {
			overlay.removeEventListener("keydown", onKey, true);
			if(overlay.parentNode) overlay.parentNode.removeChild(overlay);
			resolve(value);
		};
		const accept = () => finish(input ? input.value.trim() : true);
		const onKey = (e) => {
			if(e.key === "Escape"){ e.stopPropagation(); e.preventDefault(); finish(dismissed); }
			else if(e.key === "Enter" && !(e.target && e.target.tagName === "SELECT")){ e.stopPropagation(); e.preventDefault(); accept(); }
		};
		overlay.addEventListener("keydown", onKey, true);
		box.querySelector('[data-action="ok"]').addEventListener("click", accept);
		if(cancelBtn) cancelBtn.addEventListener("click", () => finish(dismissed));
		overlay.addEventListener("mousedown", (e) => { if(e.target === overlay) finish(dismissed); });
		(input || box.querySelector("input,select") || box.querySelector('[data-action="ok"]')).focus();
		if(input) input.select();
	});
}

function promptText(host, title, defaultValue){
	return openModal({host, title, inputValue: defaultValue || "", okLabel: "OK"});
}

function confirmAction(host, message){
	return openModal({host, title: t("Slet profil"), message, okLabel: t("Slet"), okClass: "pv-csvp-btn--danger pv-csvp-btn--solid"});
}

function sourceKey(source){
	if(source.kind === "feature") return `feature:${source.field}`;
	if(source.kind === "geometry") return `geometry:${source.field}`;
	if(source.kind === "attribute") return `attribute:${source.key}`;
	if(source.kind === "attributes-all") return "attributes-all";
	if(source.kind === "rownumber") return "rownumber";
	if(source.kind === "constant") return "constant";
	return "unknown";
}

// Minimal CSV-parser til preview-tabellen: understøtter citater ("" som escape), valgfri separator og både CRLF/LF.
// Teksten kommer fra buildCsvText() (samme pipeline som eksporten) - her parses den kun for visning.
export function parseCsvForPreview(text, separator){
	const sep = separator || ",";
	const src = String(text || "").replace(/^﻿/, "");
	const rows = [];
	let row = [];
	let cell = "";
	let inQuotes = false;
	for(let i = 0; i < src.length; i++){
		const ch = src[i];
		if(inQuotes){
			if(ch === '"'){
				if(src[i + 1] === '"'){ cell += '"'; i++; }
				else inQuotes = false;
			}else cell += ch;
		}else if(ch === '"'){
			inQuotes = true;
		}else if(src.startsWith(sep, i)){
			row.push(cell); cell = "";
			i += sep.length - 1;
		}else if(ch === "\n" || ch === "\r"){
			if(ch === "\r" && src[i + 1] === "\n") i++;
			row.push(cell); rows.push(row); row = []; cell = "";
		}else cell += ch;
	}
	if(cell !== "" || row.length){ row.push(cell); rows.push(row); }
	return rows;
}

const SEPARATOR_LABELS = {",": T("komma"), ";": T("semikolon"), "\t": T("tabulator")};

export function mountCsvProfileEditorPanel(container, digitizationModule){
	ensureStyle();
	const $container = $(container);
	$container.empty();
	$container.append($(`
		<div class="pv-csvp">
			<div class="pv-csvp-bar">
				<span class="pv-csvp-bar-label">${t("Profil")}</span>
				<select id="csvp_select" class="pv-csvp-select" aria-label="${t("CSV-profil")}"></select>
				<div class="pv-csvp-actions">
					<button type="button" class="pv-csvp-btn" id="csvp_new">${icon("plus")}${t("Ny profil")}</button>
					<button type="button" class="pv-csvp-btn" id="csvp_duplicate">${icon("copy")}${t("Kopiér")}</button>
					<button type="button" class="pv-csvp-btn" id="csvp_rename">${icon("pencil")}${t("Omdøb")}</button>
					<button type="button" class="pv-csvp-btn pv-csvp-btn--danger" id="csvp_delete">${icon("trash")}${t("Slet")}</button>
					<span class="pv-csvp-sep" aria-hidden="true"></span>
					<button type="button" class="pv-csvp-btn" id="csvp_format">${icon("tune")}${t("Formatindstillinger")}</button>
				</div>
			</div>
			<div id="csvp_status" class="pv-csvp-status" style="display:none;" role="status"></div>
			<div class="pv-csvp-grid">
				<section class="pv-csvp-card">
					<div class="pv-csvp-card-head">
						<h3>${t("Kolonner")}<span class="pv-csvp-count" id="csvp_count"></span></h3>
						<button type="button" class="pv-csvp-btn pv-csvp-btn--primary" id="csvp_add_column">${icon("plus")}${t("Tilføj kolonne")}</button>
					</div>
					<div class="pv-csvp-card-body">
						<div class="pv-csvp-colhead" aria-hidden="true">
							<span></span><span>#</span><span>${t("Kolonnenavn")}</span><span>${t("Datakilde")}</span><span></span>
						</div>
						<div id="csvp_columns_body" class="pv-csvp-columns"></div>
					</div>
				</section>
				<section class="pv-csvp-card pv-csvp-card--preview">
					<div class="pv-csvp-card-head"><h3>${t("Preview")}</h3></div>
					<div class="pv-csvp-card-body">
						<div id="csvp_preview_note" class="pv-csvp-note"></div>
						<div id="csvp_preview_body" class="pv-csvp-preview"></div>
					</div>
				</section>
			</div>
		</div>
	`));

	const elRoot = $container.find(".pv-csvp")[0];
	const elSelect = $container.find("#csvp_select");
	const elColumns = $container.find("#csvp_columns_body");
	const elPreview = $container.find("#csvp_preview_body");
	const elPreviewNote = $container.find("#csvp_preview_note");
	const elStatus = $container.find("#csvp_status");
	const elCount = $container.find("#csvp_count");

	let currentProfile = null;

	const host = () => overlayHost(elRoot);

	function showStatus(text){
		elStatus.text(text || "").toggle(!!text);
	}

	function sourceFeatures(){
		const features = digitizationModule.featureManager.features;
		return features.length ? features : buildDemoFeatures();
	}

	function refreshProfileSelect(){
		elSelect.empty();
		for(const profile of csvProfileStore.list()){
			elSelect.append($("<option></option>").val(profile.id).text(profile.name));
		}
		if(currentProfile) elSelect.val(currentProfile.id);
	}

	function persist(){
		csvProfileStore.save(currentProfile);
		renderPreview();
	}

	function loadProfile(id){
		const stored = csvProfileStore.get(id) || csvProfileStore.getDefault();
		// Dybt, uafhængigt arbejdskort - ændringer når først lageret via persist() (ved change/blur, ikke hvert tastetryk).
		currentProfile = JSON.parse(JSON.stringify(stored));
		elSelect.val(currentProfile.id);
		renderColumns();
		renderPreview();
		showStatus("");
	}

	// ---- Formatindstillinger (popup fra profillinjen; ændringer gemmes og slår igennem i preview med det samme) ----
	function buildSettingsForm(){
		const form = $(`<div class="pv-csvp-settings"></div>`);
		const isPresetSeparator = [",", ";", "\t"].indexOf(currentProfile.separator) !== -1;
		form.append($(`
			<label class="pv-csvp-field">
				<span>${t("Outputmodel")}</span>
				<select id="csvp_outputmodel">
					<option value="object">${t("Én række pr. objekt")}</option>
					<option value="vertex">${t("Én række pr. vertex")}</option>
				</select>
			</label>
			<div class="pv-csvp-field">
				<span>${t("Separator")}</span>
				<div class="pv-csvp-inline">
					<select id="csvp_separator_preset" aria-label="${t("Separator")}">
						<option value=",">${t("Komma (,)")}</option>
						<option value=";">${t("Semikolon (;)")}</option>
						<option value="\t">${t("Tabulator")}</option>
						<option value="other">${t("Andet…")}</option>
					</select>
					<input type="text" id="csvp_separator_custom" maxlength="3" placeholder="fx |" aria-label="${t("Egen separator")}" style="width:72px; flex:0 0 auto; display:none;" />
				</div>
			</div>
			<label class="pv-csvp-field">
				<span>${t("Decimaltegn")}</span>
				<input type="text" id="csvp_decimal" maxlength="1" style="max-width:96px;" />
			</label>
			<label class="pv-csvp-field">
				<span>${t("Encoding")}</span>
				<select id="csvp_encoding">
					<option value="utf8">${t("UTF-8")}</option>
					<option value="utf8bom">${t("UTF-8 BOM")}</option>
				</select>
			</label>
			<label class="pv-csvp-field">
				<span>${t("Koordinatdecimaler")}</span>
				<input type="number" id="csvp_precision" min="0" max="4" placeholder="${t("Standard (højst 4)")}" style="max-width:160px;" />
			</label>
			<label class="pv-csvp-field pv-csvp-check">
				<input type="checkbox" id="csvp_header" />
				<span>${t("Header (kolonnenavne på første linje)")}</span>
			</label>
		`));

		form.find("#csvp_outputmodel").val(currentProfile.outputModel);
		form.find("#csvp_separator_preset").val(isPresetSeparator ? currentProfile.separator : "other");
		form.find("#csvp_separator_custom").val(isPresetSeparator ? "" : currentProfile.separator).toggle(!isPresetSeparator);
		form.find("#csvp_decimal").val(currentProfile.decimalSeparator);
		form.find("#csvp_encoding").val(currentProfile.encoding);
		form.find("#csvp_header").prop("checked", !!currentProfile.header);
		form.find("#csvp_precision").val(currentProfile.coordinatePrecision === null || currentProfile.coordinatePrecision === undefined ? "" : Math.min(4, currentProfile.coordinatePrecision));

		form.find("#csvp_outputmodel").on("change", function(){ currentProfile.outputModel = $(this).val(); persist(); });
		form.find("#csvp_separator_preset").on("change", function(){
			const value = $(this).val();
			if(value === "other"){
				form.find("#csvp_separator_custom").show().trigger("focus");
				currentProfile.separator = form.find("#csvp_separator_custom").val() || ",";
			}else{
				form.find("#csvp_separator_custom").hide();
				currentProfile.separator = value;
			}
			persist();
		});
		form.find("#csvp_separator_custom").on("change", function(){
			currentProfile.separator = $(this).val() || ",";
			persist();
		});
		form.find("#csvp_decimal").on("change", function(){ currentProfile.decimalSeparator = $(this).val() || "."; persist(); });
		form.find("#csvp_encoding").on("change", function(){ currentProfile.encoding = $(this).val(); persist(); });
		form.find("#csvp_header").on("change", function(){ currentProfile.header = $(this).is(":checked"); persist(); });
		form.find("#csvp_precision").on("change", function(){
			const raw = $(this).val();
			currentProfile.coordinatePrecision = raw === "" ? null : Math.max(0, Math.min(4, parseInt(raw, 10) || 0));
			persist();
		});
		return form[0];
	}

	// Kort opsummering af formatet over preview'et, så brugeren ser det gældende format uden at åbne popup'en
	function formatSummary(){
		const p = currentProfile;
		const sep = SEPARATOR_LABELS[p.separator] ? t(SEPARATOR_LABELS[p.separator]) : `"${p.separator}"`;
		const dec = p.decimalSeparator === "," ? t("decimalkomma") : p.decimalSeparator === "." ? t("decimalpunktum") : t("decimaltegn \"{0}\"", p.decimalSeparator);
		const enc = p.encoding === "utf8bom" ? "UTF-8 BOM" : "UTF-8";
		const model = p.outputModel === "vertex" ? t("én række pr. vertex") : t("én række pr. objekt");
		return `<b>${t("Format:")}</b> ${t("{0} · separator {1} · {2} · {3}{4}", model, sep, dec, enc, p.header ? "" : t(" · uden header"))}`;
	}

	// ---- Kolonner ----
	function buildDataSourceSelect(column){
		const select = $(t('<select aria-label="Datakilde"></select>'));
		const templates = {};
		function addOption(groupEl, key, label, template){
			templates[key] = template;
			groupEl.append($("<option></option>").attr("value", key).text(label));
		}

		const gFeature = $('<optgroup label="Feature"></optgroup>');
		for(const f of FEATURE_FIELDS) addOption(gFeature, `feature:${f.field}`, f.label, {kind: "feature", field: f.field});
		select.append(gFeature);

		const gGeometry = $('<optgroup label="Geometri"></optgroup>');
		for(const f of GEOMETRY_FIELDS) addOption(gGeometry, `geometry:${f.field}`, f.label + (f.vertexOnly ? t(" (kun vertex-tilstand)") : ""), {kind: "geometry", field: f.field});
		select.append(gGeometry);

		const keys = discoverAttributeKeys(sourceFeatures());
		if(column.source.kind === "attribute" && keys.indexOf(column.source.key) === -1) keys.push(column.source.key);
		const byGroup = new Map();
		for(const key of keys){
			const groupLabel = getAttributeGroupLabel(key);
			if(!byGroup.has(groupLabel)) byGroup.set(groupLabel, $("<optgroup></optgroup>").attr("label", groupLabel));
			addOption(byGroup.get(groupLabel), `attribute:${key}`, getAttributeDisplayLabel(key), {kind: "attribute", key});
		}
		for(const groupEl of byGroup.values()) select.append(groupEl);

		const gOther = $('<optgroup label="Andet"></optgroup>');
		addOption(gOther, "rownumber", t("Fortløbende nummer (1, 2, 3 …)"), {kind: "rownumber"});
		addOption(gOther, "attributes-all", t("Alle attributter (dynamisk)"), {kind: "attributes-all"});
		addOption(gOther, "constant", t("Fast værdi"), {kind: "constant", value: ""});
		select.append(gOther);

		select.val(sourceKey(column.source));
		select.data("templates", templates);
		return select;
	}

	// Flyt kolonne `from` så den ender på plads `to` (indeks i listen efter flytningen)
	function moveColumn(from, to){
		const cols = currentProfile.columns;
		if(from === to || from < 0 || to < 0 || from >= cols.length || to >= cols.length) return;
		cols.splice(to, 0, cols.splice(from, 1)[0]);
		persist();
		renderColumns();
	}

	// Én kolonne = én enhed: håndtag (drag/drop) | nummer | kolonnenavn | datakilde (+ fast værdi) | slet
	function renderColumnRow(column, index, total){
		const row = $(`<div class="pv-csvp-col" data-col="${index}"></div>`);
		const handle = $(`<button type="button" class="pv-csvp-handle" title="${t("Træk for at flytte (eller Alt + pil op/ned)")}" aria-label="${t("Flyt kolonne {0}", index + 1)}">${icon("grip")}</button>`);
		const no = $(`<span class="pv-csvp-col-no"></span>`).text(index + 1);
		const headerInput = $(`<input type="text" placeholder="${t("Kolonnenavn")}" aria-label="${t("Kolonnenavn")}" />`).val(column.header);

		const sourceWrap = $(`<div class="pv-csvp-col-source"></div>`);
		const sourceSelect = buildDataSourceSelect(column);
		const constantInput = $(`<input type="text" placeholder="${t("Fast værdi")}" aria-label="${t("Fast værdi")}" />`)
			.val(column.source.kind === "constant" ? column.source.value : "")
			.toggle(column.source.kind === "constant");
		sourceWrap.append(sourceSelect, constantInput);

		const delBtn = $(`<button type="button" class="pv-csvp-iconbtn" title="${t("Fjern kolonne")}" aria-label="${t("Fjern kolonne")}">${icon("trash")}</button>`);

		row.append(handle, no, headerInput, sourceWrap, delBtn);

		headerInput.on("change", () => { column.header = headerInput.val() || column.header; headerInput.val(column.header); persist(); });
		sourceSelect.on("change", () => {
			const key = sourceSelect.val();
			const template = sourceSelect.data("templates")[key];
			column.source = {...template};
			constantInput.toggle(column.source.kind === "constant");
			if(column.source.kind === "constant") constantInput.trigger("focus");
			persist();
		});
		constantInput.on("change", () => { column.source.value = constantInput.val(); persist(); });
		delBtn.on("click", () => {
			currentProfile.columns.splice(index, 1);
			persist();
			renderColumns();
		});

		// Tastatur: Alt + pil op/ned på håndtaget flytter kolonnen (og beholder fokus på håndtaget)
		handle.on("keydown", (e) => {
			if(!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
			e.preventDefault();
			const to = e.key === "ArrowUp" ? index - 1 : index + 1;
			if(to < 0 || to >= total) return;
			moveColumn(index, to);
			elColumns.find(`.pv-csvp-col[data-col="${to}"] .pv-csvp-handle`).trigger("focus");
		});

		return row;
	}

	function renderColumns(){
		elColumns.empty();
		const total = currentProfile.columns.length;
		elCount.text(String(total));
		if(total === 0){
			elColumns.append($(`<div class="pv-csvp-empty">${t("Ingen kolonner endnu. Brug <b>Tilføj kolonne</b> for at komme i gang.")}</div>`));
			return;
		}
		currentProfile.columns.forEach((column, index) => elColumns.append(renderColumnRow(column, index, total)));
	}

	// ---- Drag/drop af kolonner: pointer-baseret (ikke HTML5-DnD, som er upålideligt i dialoger/lister). Man trækker i
	// håndtaget; rækken følger musen, en indikatorlinje viser hvor den lander, og listen ruller automatisk ved kanten.
	// Pladsen beregnes ud fra de ANDRE rækkers midtpunkter, så det også virker mellem rækkerne og uden for listen. ----
	const EDGE = 36; // px fra listens top/bund hvor der auto-rulles
	function startDrag(downEvent, handleEl, rowEl, from){
		const list = elColumns[0];
		const rows = elColumns.find(".pv-csvp-col").toArray();
		const others = rows.filter((r) => r !== rowEl);
		const startY = downEvent.clientY;
		const startScroll = list.scrollTop;
		let lastY = startY;
		let to = from;
		let raf = 0;
		rowEl.classList.add("is-dragging");
		if(handleEl.setPointerCapture) handleEl.setPointerCapture(downEvent.pointerId);

		const update = () => {
			const y = lastY;
			const dy = (y - startY) + (list.scrollTop - startScroll);
			rowEl.style.transform = `translateY(${dy}px)`;
			to = others.filter((r) => { const b = r.getBoundingClientRect(); return b.top + b.height / 2 < y; }).length;
			elColumns.find(".pv-csvp-col").removeClass("drop-before drop-after");
			if(to < others.length) others[to].classList.add("drop-before");
			else if(others.length) others[others.length - 1].classList.add("drop-after");
		};
		const autoScroll = () => {
			const b = list.getBoundingClientRect();
			let step = 0;
			if(lastY < b.top + EDGE) step = -Math.ceil((b.top + EDGE - lastY) / 4);
			else if(lastY > b.bottom - EDGE) step = Math.ceil((lastY - (b.bottom - EDGE)) / 4);
			if(step){ list.scrollTop += Math.max(-24, Math.min(24, step)); update(); }
			raf = requestAnimationFrame(autoScroll);
		};
		raf = requestAnimationFrame(autoScroll);

		const cleanup = () => {
			cancelAnimationFrame(raf);
			handleEl.removeEventListener("pointermove", onMove);
			handleEl.removeEventListener("pointerup", onUp);
			handleEl.removeEventListener("pointercancel", onCancel);
			handleEl.removeEventListener("keydown", onKey);
			if(handleEl.releasePointerCapture){ try{ handleEl.releasePointerCapture(downEvent.pointerId); }catch(e){ /* allerede frigivet */ } }
			rowEl.classList.remove("is-dragging");
			rowEl.style.transform = "";
			elColumns.find(".pv-csvp-col").removeClass("drop-before drop-after");
		};
		const onMove = (e) => { lastY = e.clientY; update(); };
		const onUp = (e) => {
			lastY = e.clientY;
			update();
			const target = to;
			cleanup();
			moveColumn(from, target);
		};
		const onCancel = () => cleanup();
		const onKey = (e) => { if(e.key === "Escape"){ e.stopPropagation(); cleanup(); } };
		handleEl.addEventListener("pointermove", onMove);
		handleEl.addEventListener("pointerup", onUp);
		handleEl.addEventListener("pointercancel", onCancel);
		handleEl.addEventListener("keydown", onKey);
		update();
	}
	elColumns.on("pointerdown", ".pv-csvp-handle", function(e){
		const ev = e.originalEvent;
		if(ev.button !== 0) return;
		ev.preventDefault(); // ingen tekstmarkering under træk
		this.focus();
		const row = $(this).closest(".pv-csvp-col");
		startDrag(ev, this, row[0], Number(row.attr("data-col")));
	});

	// ---- Preview ----
	function renderPreview(){
		elPreview.empty();
		const features = digitizationModule.featureManager.features;
		const demo = features.length === 0;
		const text = buildCsvText(currentProfile, sourceFeatures(), {maxRows: 8});
		elPreviewNote.html(`${demo ? t("Eksempeldata - der er ingen registreringer endnu.") : t("Første 8 rækker af dine registreringer.")}<br>${formatSummary()}`);

		const rows = parseCsvForPreview(text, currentProfile.separator);
		const colCount = rows.reduce((max, r) => Math.max(max, r.length), 0);
		if(!rows.length || colCount === 0){
			elPreview.append($(`<div class="pv-csvp-empty" style="border:none;">${t("Intet at vise endnu.")}</div>`));
			return;
		}

		// kolonnenumrene svarer til nummeret i kolonnelisten (kun ved samme antal, fx ikke ved "Alle attributter")
		const mapped = colCount === currentProfile.columns.length;
		const cell = (tag, c, text) => {
			const el = $(`<${tag}></${tag}>`).text(text);
			if(mapped) el.attr("data-col", c);
			return el;
		};
		const table = $('<table class="pv-csvp-table"></table>');
		const thead = $("<thead></thead>");
		const numRow = $("<tr></tr>");
		for(let c = 0; c < colCount; c++) numRow.append(cell("th", c, mapped ? String(c + 1) : "·"));
		thead.append(numRow);
		let bodyRows = rows;
		if(currentProfile.header){
			const headRow = $("<tr></tr>");
			for(let c = 0; c < colCount; c++) headRow.append(cell("th", c, rows[0][c] === undefined ? "" : rows[0][c]));
			thead.append(headRow);
			bodyRows = rows.slice(1);
		}
		table.append(thead);
		const tbody = $("<tbody></tbody>");
		for(const r of bodyRows){
			const tr = $("<tr></tr>");
			for(let c = 0; c < colCount; c++) tr.append(cell("td", c, r[c] === undefined ? "" : r[c]));
			tbody.append(tr);
		}
		table.append(tbody);
		elPreview.append(table);
	}

	// Hover: kolonnen markeres både i listen og i preview-tabellen
	function setHot(index){
		elColumns.find(".pv-csvp-col").each(function(){ $(this).toggleClass("is-hot", index !== null && String($(this).data("col")) === String(index)); });
		elPreview.find("[data-col]").each(function(){ $(this).toggleClass("is-hot", index !== null && String($(this).attr("data-col")) === String(index)); });
	}
	elColumns.on("mouseover", ".pv-csvp-col", function(){ setHot($(this).data("col")); });
	elColumns.on("mouseleave", () => setHot(null));
	elPreview.on("mouseover", "[data-col]", function(){ setHot($(this).attr("data-col")); });
	elPreview.on("mouseleave", () => setHot(null));

	// ---- Profilhåndtering ----
	elSelect.on("change", () => loadProfile(elSelect.val()));

	$container.find("#csvp_format").on("click", async () => {
		await openModal({host: host(), title: t("Formatindstillinger"), body: buildSettingsForm(), okLabel: t("Færdig"), cancelLabel: ""});
		renderPreview();
	});

	$container.find("#csvp_add_column").on("click", () => {
		currentProfile.columns.push(createColumn(t("Ny kolonne"), {kind: "feature", field: "name"}));
		persist();
		renderColumns();
		// ny kolonne: rul til den og sæt markøren i navnefeltet, så den kan døbes med det samme
		const last = elColumns.find(".pv-csvp-col").last();
		if(last.length){
			last[0].scrollIntoView({block: "nearest"});
			last.find("input[type=text]").first().trigger("focus").trigger("select");
		}
	});

	// Ny profil: brugeren navngiver den, og den "fødes med alle muligheder" (ID med fortløbende nummer + alle felter +
	// attributter), så ikke-ønskede kolonner bare slettes.
	$container.find("#csvp_new").on("click", async () => {
		const suggested = t("Ny profil {0}", csvProfileStore.list().length + 1);
		const name = await promptText(host(), t("Navn på ny profil"), suggested);
		if(name === null) return; // annulleret
		const profile = createFullProfile(name || suggested);
		csvProfileStore.save(profile);
		refreshProfileSelect();
		loadProfile(profile.id);
		showStatus(t("Profilen indeholder alle kolonner - slet dem, der ikke skal med."));
	});

	$container.find("#csvp_rename").on("click", async () => {
		const name = await promptText(host(), t("Omdøb profil"), currentProfile.name);
		if(!name) return;
		csvProfileStore.rename(currentProfile.id, name);
		currentProfile.name = name;
		refreshProfileSelect();
	});

	$container.find("#csvp_duplicate").on("click", async () => {
		const name = await promptText(host(), t("Navn på kopi"), t("{0} (kopi)", currentProfile.name));
		if(name === null) return;
		const copy = csvProfileStore.duplicate(currentProfile.id, name || undefined);
		refreshProfileSelect();
		loadProfile(copy.id);
	});

	$container.find("#csvp_delete").on("click", async () => {
		if(csvProfileStore.list().length <= 1){
			showStatus(t("Den sidste profil kan ikke slettes."));
			return;
		}
		const ok = await confirmAction(host(), t("Slet profilen \"{0}\"? Dette kan ikke fortrydes.", currentProfile.name));
		if(!ok) return;
		csvProfileStore.delete(currentProfile.id);
		refreshProfileSelect();
		loadProfile(csvProfileStore.getDefault().id);
	});

	refreshProfileSelect();
	loadProfile(csvProfileStore.getDefault().id);

	return {
		unmount(){
			// Alle lyttere sidder på elementer inde i `container`; det rydder containeren. Intet eksternt (digitizationModule/
			// store-lyttere) er registreret, og den injicerede <style> er delt og harmløs at beholde.
			$container.empty();
		},
	};
}
